import { config as defaultConfig } from '@tamagui/config/v3';
import { createTamagui, createTokens } from 'tamagui';

/**
 * CarTrack Pro design tokens — slice 1 of the visual refresh.
 *
 * Dark, minimal, Apple.com-like. We extend the stock Tamagui v3 config rather
 * than replacing it so existing usage of `$color`, `$4`, `$background`, etc.
 * keeps working. We only ADD a named brand accent + a cohesive slate scale and
 * re-point the dark theme's key surfaces at them.
 *
 * The single accent (`$accent` / `tokens.color.accent`) replaces the previously
 * hardcoded #2997ff / #22d3ee hex strings scattered across components.
 */

// Brand + neutral palette. Slate scale matches the current dark UI
// (page bg ~#0B0F19 / #0f172a). `accent` is the user-approved #2997ff.
const palette = {
  accent: '#2997ff',
  accentHover: '#4aa9ff',
  accentPress: '#1f7fe0',

  // Slate / neutral scale, dark -> light.
  slate1: '#0B0F19', // deepest background
  slate2: '#0f172a', // app background
  slate3: '#16203a', // raised surface
  slate4: '#1e293b', // card / hover surface
  slate5: '#293548', // borders, dividers
  slate6: '#3b475c',
  slate7: '#56627a',
  slate8: '#8a94a6', // muted text
  slate9: '#aab2c0',
  slate10: '#cbd5e1', // secondary text
  slate11: '#e2e8f0', // primary text
  slate12: '#f8fafc', // high-emphasis text / headings

  danger: '#ef4444',
  success: '#22c55e',
  warning: '#f59e0b',
  white: '#ffffff',
  black: '#000000',
} as const;

// Light neutral scale — the inverse register of the dark slate scale. Near-white
// surfaces, dark text, hairline cool-gray borders. The accent stays #2997ff so
// the brand reads identically in both modes. Indices mirror `slate1..slate12`
// (1 = deepest surface, 12 = highest-emphasis text) so theme keys map cleanly.
const lightPalette = {
  // Surfaces, lightest -> slightly raised.
  l1: '#ffffff', // page background
  l2: '#f7f8fa', // app background / subtle fill
  l3: '#eef1f5', // raised surface / hover
  l4: '#e4e8ee', // card hover / pressed surface
  l5: '#d7dde6', // borders, dividers (hairline)
  l6: '#c2cad6',
  l7: '#9aa4b4',
  l8: '#606b7a', // muted text — AA on #fff (5.41:1) and #f7f8fa (5.09:1)
  l9: '#55606f',
  l10: '#3f4855', // secondary text
  l11: '#26303c', // primary text
  l12: '#0d1620', // high-emphasis text / headings
} as const;

// Extend (do not replace) the default tokens so `$4`, `$true`, etc. survive.
export const tokens = createTokens({
  ...defaultConfig.tokens,
  color: {
    ...defaultConfig.tokens.color,
    accent: palette.accent,
    accentHover: palette.accentHover,
    accentPress: palette.accentPress,
    slate1: palette.slate1,
    slate2: palette.slate2,
    slate3: palette.slate3,
    slate4: palette.slate4,
    slate5: palette.slate5,
    slate6: palette.slate6,
    slate7: palette.slate7,
    slate8: palette.slate8,
    slate9: palette.slate9,
    slate10: palette.slate10,
    slate11: palette.slate11,
    slate12: palette.slate12,
    danger: palette.danger,
    success: palette.success,
    warning: palette.warning,
    white: palette.white,
    black: palette.black,
  },
  // Spacing + size + radius scales come from the default config; we keep them so
  // `$2`, `$3`, `$4` spacing tokens used across the app are unchanged.
  space: defaultConfig.tokens.space,
  size: defaultConfig.tokens.size,
  radius: defaultConfig.tokens.radius,
  zIndex: defaultConfig.tokens.zIndex,
});

// Re-point the dark theme onto our slate scale + accent. We spread the stock
// dark theme first so every key the framework expects still exists.
const darkTheme = {
  ...defaultConfig.themes.dark,
  background: palette.slate2,
  backgroundHover: palette.slate3,
  backgroundPress: palette.slate4,
  backgroundFocus: palette.slate3,
  backgroundStrong: palette.slate1,
  backgroundTransparent: 'rgba(11,15,25,0)',

  color: palette.slate11,
  colorHover: palette.slate12,
  colorPress: palette.slate10,
  colorFocus: palette.slate12,

  borderColor: palette.slate5,
  borderColorHover: palette.slate6,
  borderColorFocus: palette.accent,
  borderColorPress: palette.slate6,

  // Convenience semantic aliases used by the refreshed components.
  accent: palette.accent,
  accentHover: palette.accentHover,
  accentPress: palette.accentPress,

  color1: palette.slate1,
  color2: palette.slate2,
  color3: palette.slate3,
  color4: palette.slate4,
  color5: palette.slate5,
  color6: palette.slate6,
  color7: palette.slate7,
  color8: palette.slate8,
  color9: palette.slate9,
  color10: palette.slate10,
  color11: palette.slate11,
  color12: palette.slate12,
};

// Branded light theme — mirror of darkTheme onto the light neutral scale. We
// spread the stock light theme first so every framework key still exists, then
// re-point surfaces, text, and borders. Same accent as dark mode.
const lightTheme = {
  ...defaultConfig.themes.light,
  background: lightPalette.l1,
  backgroundHover: lightPalette.l3,
  backgroundPress: lightPalette.l4,
  backgroundFocus: lightPalette.l3,
  backgroundStrong: lightPalette.l2,
  backgroundTransparent: 'rgba(255,255,255,0)',

  color: lightPalette.l11,
  colorHover: lightPalette.l12,
  colorPress: lightPalette.l10,
  colorFocus: lightPalette.l12,

  borderColor: lightPalette.l5,
  borderColorHover: lightPalette.l6,
  borderColorFocus: palette.accent,
  borderColorPress: lightPalette.l6,

  accent: palette.accent,
  accentHover: palette.accentHover,
  accentPress: palette.accentPress,

  // color1..color12 map deepest-surface -> highest-emphasis-text, matching the
  // dark theme's convention so components using `$color2`, `$color10`, etc.
  // render correctly in both modes.
  color1: lightPalette.l1,
  color2: lightPalette.l2,
  color3: lightPalette.l3,
  color4: lightPalette.l4,
  color5: lightPalette.l5,
  color6: lightPalette.l6,
  color7: lightPalette.l7,
  color8: lightPalette.l8,
  color9: lightPalette.l9,
  color10: lightPalette.l10,
  color11: lightPalette.l11,
  color12: lightPalette.l12,
};

export const config = createTamagui({
  ...defaultConfig,
  tokens,
  themes: {
    ...defaultConfig.themes,
    dark: darkTheme,
    light: lightTheme,
  },
  // media queries (`$sm`, `$gtSm`, etc.) come from the default config and drive
  // the responsive nav. Listed here for clarity; not overridden.
  media: defaultConfig.media,
});

export default config;

export type AppConfig = typeof config;

declare module 'tamagui' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface TamaguiCustomConfig extends AppConfig {}
}
